import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { READONLY_LINK_STYLES } from './link-styles';
import { ValueDisplayComponent } from './value-display.component';

/** One stored address: what it says, and the `mailto:` it opens. */
interface EmailAddressLink {
  href: string;
  text: string;
}

/**
 * Read display for `email-tags` (#21239, task 6): every stored address becomes its own
 * `mailto:` link, the way the single-address `email` type already reads through
 * `LinkDisplayComponent`. Multiple addresses is the entire reason this field type exists over
 * plain `email` — printing them as one comma-joined string (the previous
 * `ValueDisplayComponent` fallback, still what `formatDisplayValue`'s generic array join
 * produces) left every address but the first unreachable.
 *
 * Follows `LinkedEntryDisplayComponent`'s per-item pattern — a part per stored item, a text
 * separator between them — rather than a second variant: that display already renders a LIST
 * with some parts linked and some not, which is exactly this shape minus the route lookup.
 * Styling is the shared `READONLY_LINK_STYLES` (`link-styles.ts`), not a third copy of it.
 *
 * Presentational: the only input is the row's own value.
 */
@Component({
  selector: 'dd-email-tags-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: READONLY_LINK_STYLES,
  // The ', ' separator is a real text node, not a CSS ::after — matching
  // `LinkedEntryDisplayComponent` and the join `formatDisplayValue` uses for every other
  // multi-value row, so a copy-paste or a screen reader gets the same list either way.
  template: `
    @if (links(); as links) {
      @for (link of links; track $index) {
        <a [href]="link.href">{{ link.text }}</a>
        @if (!$last) {
          <span>, </span>
        }
      }
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class EmailTagsDisplayComponent extends ReadonlyDisplayBase {
  /**
   * One link per stored address, or `null` for "let the text display handle it" — an empty
   * answer (which has to read as the row's em dash) or a value with no addressable text in it
   * at all. `email-tags` stores `string[]` (`email-tags.component.ts`), but the field component
   * that normalises a legacy plain-string value onto load is never built in read mode (it is a
   * wrappable type — `ReadonlyWrapper` replaces it with this row entirely), so a value left over
   * from before it existed can still be a bare string; that is handled the same as a one-item
   * array rather than treated as unreadable.
   */
  readonly links = computed<EmailAddressLink[] | null>(() => {
    const raw = this.value();
    const items = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];

    const links = items
      .map(item => (typeof item === 'string' ? item.trim() : ''))
      .filter(text => text !== '')
      .map(text => ({ href: `mailto:${text}`, text }));

    return links.length ? links : null;
  });
}
